import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Camera, UploadCloud } from "lucide-react";
import { Button } from "./ui/button";

const ANGLE_GUIDE = {
  front: { instruction: "Face the camera directly and center your face inside the oval.", rotate: 0 },
  left: { instruction: "Slowly turn your head to show your LEFT profile, matching the tilted oval.", rotate: -22 },
  right: { instruction: "Slowly turn your head to show your RIGHT profile, matching the tilted oval.", rotate: 22 },
};

export const FaceCaptureModal = ({ angle, label, busy, onClose, onCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraError, setCameraError] = useState(false);
  const guide = ANGLE_GUIDE[angle] || ANGLE_GUIDE.front;

  useEffect(() => {
    let active = true;
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(true);
      return;
    }
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "user" } })
      .then((stream) => {
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => setCameraError(true));
    return () => {
      active = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => blob && onCapture(blob), "image/jpeg", 0.92);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) onCapture(file);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      data-testid={`face-capture-modal-${angle}`}
      onClick={onClose}
      role="dialog"
    >
      <div
        className="w-full max-w-md rounded-lg border border-[#D4AF37]/40 bg-obsidian p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <p className="font-heading text-lg font-bold text-white">Capture {label} Angle</p>
          <button onClick={onClose} data-testid={`face-capture-close-${angle}`} className="text-slate-400 hover:text-white" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-3 text-sm text-slate-400">{guide.instruction}</p>

        {!cameraError ? (
          <div className="relative mb-3 overflow-hidden rounded-md border border-surface-border bg-black" style={{ aspectRatio: "4/3" }}>
            <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover" data-testid={`face-capture-video-${angle}`} />
            <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <ellipse
                cx="50"
                cy="48"
                rx="26"
                ry="34"
                transform={`rotate(${guide.rotate} 50 48)`}
                fill="none"
                stroke="#D4AF37"
                strokeWidth="1.2"
                strokeDasharray="3 2"
                opacity="0.85"
              />
            </svg>
          </div>
        ) : (
          <p className="mb-3 rounded border border-sovereign/40 bg-sovereign/5 p-2 text-xs text-sovereign" data-testid={`face-capture-camera-error-${angle}`}>
            Camera unavailable or permission denied - upload a photo instead.
          </p>
        )}
        <canvas ref={canvasRef} className="hidden" />

        <div className="flex flex-wrap gap-2">
          {!cameraError && (
            <Button disabled={busy} onClick={capture} className="gap-2 bg-[#D4AF37] text-obsidian hover:bg-[#D4AF37]/90" data-testid={`face-capture-btn-${angle}`}>
              <Camera className="h-4 w-4" /> Capture
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            onChange={handleFile}
            className="hidden"
            data-testid={`face-sample-file-input-${angle}`}
          />
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            className="gap-2 border-surface-border text-slate-200 hover:text-white"
            data-testid={`face-capture-upload-fallback-${angle}`}
          >
            <UploadCloud className="h-4 w-4" /> Upload Instead
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};
